import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Voicemail, MessageSquare, RefreshCw, ArrowLeft, Phone, Search, Link2, X, Archive as ArchiveIcon, ExternalLink, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECEIVED_BY_OPTIONS = ['', 'Main Office', 'Joao', 'Gabi', 'Scheduling Dept'];
const STATUS_OPTIONS = [
  { value: '', label: 'Active (not archived)' },
  { value: 'NEW', label: 'NEW' },
  { value: 'LINKED', label: 'LINKED' },
  { value: 'WORKED', label: 'WORKED' },
  { value: 'ARCHIVED', label: 'ARCHIVED' },
  { value: 'ALL', label: 'All' },
];

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const Pill = ({ children, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
    slate: 'bg-slate-200 text-slate-600',
  };
  return <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${tones[tone] || tones.gray}`}>{children}</span>;
};

const ReceivedByPill = ({ name }) => {
  const tone = { 'Main Office': 'blue', 'Joao': 'green', 'Gabi': 'purple', 'Scheduling Dept': 'amber' }[name] || 'gray';
  return <Pill tone={tone}>{name || '—'}</Pill>;
};

const AppStatusPill = ({ status }) => {
  const t = { NEW: 'gray', LINKED: 'blue', WORKED: 'green', ARCHIVED: 'slate' }[status] || 'gray';
  return <Pill tone={t}>{status || 'NEW'}</Pill>;
};

const RcInboxLayoutHeader = ({ title, icon, total, syncing, onSync, onBack }) => (
  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
    <div className="flex items-center gap-3">
      <button onClick={onBack} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600" data-testid="rc-inbox-back-btn" title="Back to Scheduling">
        <ArrowLeft className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2">
        {icon}
        <h1 className="text-xl font-bold text-gray-800">{title}</h1>
        <span className="text-sm text-gray-500">({total})</span>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button onClick={onSync} disabled={syncing}
        className="px-3 py-1.5 bg-[#6B8F62] hover:bg-[#5A7B52] disabled:opacity-50 text-white text-sm font-medium rounded-lg flex items-center gap-1.5"
        data-testid="rc-inbox-sync-btn">
        <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
        {syncing ? 'Syncing…' : 'Sync now'}
      </button>
    </div>
  </div>
);

const FilterBar = ({ direction, setDirection, receivedBy, setReceivedBy, status, setStatus, showDirection = true }) => (
  <div className="flex items-center gap-3 mb-3 flex-wrap">
    {showDirection && (
      <div className="flex items-center gap-1.5">
        <label className="text-xs text-gray-500">Direction:</label>
        <select value={direction} onChange={(e) => setDirection(e.target.value)} className="text-xs border border-gray-300 rounded-md px-2 py-1" data-testid="rc-inbox-filter-direction">
          <option value="">Inbound only (default)</option>
          <option value="outbound">Outbound only</option>
          <option value="all">All</option>
        </select>
      </div>
    )}
    <div className="flex items-center gap-1.5">
      <label className="text-xs text-gray-500">Received by:</label>
      <select value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} className="text-xs border border-gray-300 rounded-md px-2 py-1" data-testid="rc-inbox-filter-received-by">
        {RECEIVED_BY_OPTIONS.map((o) => (<option key={o || 'all'} value={o}>{o || 'All'}</option>))}
      </select>
    </div>
    <div className="flex items-center gap-1.5">
      <label className="text-xs text-gray-500">Status:</label>
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs border border-gray-300 rounded-md px-2 py-1" data-testid="rc-inbox-filter-status">
        {STATUS_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Detail modal — RingCentral is the entry point. Final activity registration
// happens inside the Order Detail page (handoff via location.state).
// ─────────────────────────────────────────────────────────────────────────
const RcDetailModal = ({ kind, item, onClose, onChanged }) => {
  const isSms = kind === 'sms';
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(item.matched_order_id ? {
    order_id: item.matched_order_id,
    order_number: item.matched_order_number || '',
    address: item.matched_order_address || '',
  } : null);
  const [linking, setLinking] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState('');
  const [thread, setThread] = useState({ counterparty: '', items: [] });
  const [loadingThread, setLoadingThread] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  // SMS Reply (only inbound SMS get this UI)
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyResult, setReplyResult] = useState(null);
  const searchTimerRef = useRef(null);

  // Auto-load SMS thread (full conversation by phone)
  useEffect(() => {
    if (!isSms) return;
    setLoadingThread(true);
    axios.get(`${API}/rc-inbox/sms/${item.rc_id}/thread`, { params: { limit: 200 } })
      .then((r) => setThread(r.data || { counterparty: '', items: [] }))
      .catch(() => setThread({ counterparty: '', items: [] }))
      .finally(() => setLoadingThread(false));
  }, [isSms, item.rc_id]);

  // Angela Feb 2026 (Linked Order binding fix — STRICT Option A):
  // The Linked Order search field MUST open empty. Operators type the
  // Order ID manually.  The previous seed populated the input with the
  // conversation phone number (last 10 digits of `from_number`) which
  // caused /rc-inbox/orders/search to look up by phone — creating the
  // perception of a phone-based binding even though the POST
  // /rc-inbox/.../link-order always uses order_id correctly.
  // Block intentionally removed; kept as a no-op useEffect to minimise
  // the diff and preserve the existing hook order for React.
  useEffect(() => {
    // intentional no-op — no phone-based seed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  useEffect(() => {
    if (selectedOrder) return;
    if (!search || search.trim().length < 2) { setSearchResults([]); return; }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/rc-inbox/orders/search`, { params: { q: search.trim(), limit: 10 } });
        setSearchResults(r.data.items || []);
      } catch (e) { console.error(e); }
      finally { setSearching(false); }
    }, 350);
  }, [search, selectedOrder]);

  const linkOrder = async (order) => {
    setLinking(true); setError('');
    try {
      await axios.post(`${API}/rc-inbox/${kind}/${item.rc_id}/link-order`, { order_id: order.order_id });
      setSelectedOrder(order);
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Link failed');
    } finally { setLinking(false); }
  };

  const unlinkOrder = () => { setSelectedOrder(null); setSearchResults([]); };

  const openOrder = () => {
    if (!selectedOrder) return;
    const referenceText = isSms
      ? `[RC SMS · ${item.received_by_name || ''}] ${item.text || ''}`
      : `[RC Voicemail · ${item.received_by_name || ''}] ${item.vm_transcription_text || '(no transcription)'}`;
    navigate(`/scheduling/order/${selectedOrder.order_id}`, {
      state: {
        rcInboxContext: {
          kind,
          rc_id: item.rc_id,
          received_by: item.received_by_name || '',
          from_number: item.from_number || '',
          referenceText,
        },
      },
    });
  };

  const archive = async () => {
    setArchiving(true); setError('');
    try {
      await axios.post(`${API}/rc-inbox/${kind}/${item.rc_id}/archive`);
      onChanged && onChanged();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Archive failed');
      setArchiving(false);
    }
  };

  const loadAudio = async () => {
    setAudioLoading(true);
    try {
      const r = await axios.get(`${API}/rc-inbox/voicemail/${item.rc_id}/audio`, { responseType: 'blob' });
      setAudioUrl(URL.createObjectURL(new Blob([r.data], { type: r.headers['content-type'] || 'audio/mpeg' })));
    } catch (e) { console.error(e); }
    finally { setAudioLoading(false); }
  };

  const ourExtensionDigits = ['4014156278', '4013820425', '4013820233', '4014046542'];
  const isOurNumber = (n) => {
    const d = (n || '').replace(/\D/g, '').slice(-10);
    return ourExtensionDigits.includes(d);
  };

  // Map received_by_name → reply-from line for the operator to see clearly.
  const replyFromLine = isSms ? ({
    'Main Office': '+1 401-415-6278 (Main Office)',
    'Joao': '+1 401-382-0425 (João)',
    'Gabi': '+1 401-382-0233 (Gabi)',
    'Scheduling Dept': '+1 401-404-6542 (Scheduling Dept)',
  }[item.received_by_name] || (item.received_by_name || 'auto-detected line')) : '';

  const sendReply = async () => {
    const txt = (replyText || '').trim();
    if (!txt) return;
    setReplySending(true); setReplyResult(null); setError('');
    try {
      const r = await axios.post(`${API}/rc-inbox/sms/${item.rc_id}/reply`, { text: txt });
      setReplyResult({ ok: true, ...r.data });
      setReplyText('');
      // Reload thread to show the just-sent reply
      try {
        const t = await axios.get(`${API}/rc-inbox/sms/${item.rc_id}/thread`, { params: { limit: 200 } });
        setThread(t.data || { counterparty: '', items: [] });
      } catch {}
    } catch (e) {
      setReplyResult({ ok: false, error: e?.response?.data?.detail || 'Send failed' });
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto" data-testid="rc-inbox-detail-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-4">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isSms ? <MessageSquare className="h-5 w-5 text-[#6B8F62]" /> : <Voicemail className="h-5 w-5 text-[#9A7A3E]" />}
            <h2 className="font-semibold text-gray-800">{isSms ? 'SMS · RingCentral' : 'Voicemail · RingCentral'}</h2>
            <AppStatusPill status={item.app_status || 'NEW'} />
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="rc-inbox-detail-close">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Item details */}
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 rounded p-3">
            <div><span className="text-gray-400">When: </span>{fmtDateTime(item.creation_time_rc)}</div>
            <div><span className="text-gray-400">Direction: </span>{item.direction || 'inbound'}</div>
            <div><span className="text-gray-400">From: </span><span className="font-mono">{item.from_number || '—'}</span></div>
            <div><span className="text-gray-400">To: </span><span className="font-mono">{item.to_number || '—'}</span></div>
            <div className="col-span-2"><span className="text-gray-400">Received by: </span><ReceivedByPill name={item.received_by_name} /></div>
            {!isSms && (
              <>
                <div><span className="text-gray-400">Duration: </span>{item.vm_duration ? `${item.vm_duration}s` : '—'}</div>
                <div><span className="text-gray-400">Transcription: </span>{item.vm_transcription_status || '—'}</div>
              </>
            )}
          </div>

          {/* Voicemail audio + transcription */}
          {!isSms && (
            <>
              <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                {audioUrl ? (
                  <audio controls src={audioUrl} className="w-full" data-testid="rc-inbox-vm-audio" />
                ) : (
                  <button onClick={loadAudio} disabled={audioLoading || !item.audio_attachment_id}
                    className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                    {audioLoading ? 'Loading…' : (item.audio_attachment_id ? 'Load audio' : 'No audio')}
                  </button>
                )}
              </div>
              {item.vm_transcription_text && (
                <div className="bg-white border border-gray-200 rounded p-3 text-sm whitespace-pre-wrap" data-testid="rc-inbox-vm-transcription">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Transcription</div>
                  {item.vm_transcription_text}
                </div>
              )}
            </>
          )}

          {/* SMS conversation thread */}
          {isSms && (
            <div className="bg-white border border-gray-200 rounded" data-testid="rc-sms-thread">
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                <div className="text-[10px] uppercase tracking-wider text-gray-400">
                  Conversation with <span className="font-mono text-gray-700">{thread.counterparty || item.from_number}</span>
                </div>
                <span className="text-[10px] text-gray-400">{thread.items.length} messages</span>
              </div>
              <div className="max-h-72 overflow-y-auto p-3 space-y-2">
                {loadingThread && <div className="text-xs text-gray-400">Loading…</div>}
                {!loadingThread && thread.items.length === 0 && (
                  <div className="text-xs text-gray-400">Just this single message — no other history.</div>
                )}
                {!loadingThread && thread.items.map((m) => {
                  const mine = isOurNumber(m.from_number);
                  return (
                    <div key={m.rc_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-xs ${mine ? 'bg-[#6B8F62]/10 border border-[#6B8F62]/30' : 'bg-gray-100 border border-gray-200'}`}>
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mb-0.5">
                          <span>{fmtDateTime(m.creation_time_rc)}</span>
                          <span className="text-gray-300">·</span>
                          <span>{mine ? 'us' : 'them'}</span>
                          {mine && m.received_by_name && <><span className="text-gray-300">·</span><ReceivedByPill name={m.received_by_name} /></>}
                          {m.rc_id === item.rc_id && <span className="ml-1 text-[#6B8F62] font-semibold">← this message</span>}
                        </div>
                        <div className="whitespace-pre-wrap">{m.text || <span className="text-gray-400 italic">(empty)</span>}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* SMS Reply (inline) — RingCentral-only, optional, does NOT archive */}
        {isSms && (item.direction || 'inbound') === 'inbound' && (
          <div className="px-4 pb-4" data-testid="rc-inbox-reply-section">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] uppercase tracking-wider text-gray-400">Quick Reply (RingCentral)</p>
              <span className="text-[10px] text-gray-500">From: <b className="font-mono">{replyFromLine}</b></span>
            </div>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              maxLength={1500}
              rows={3}
              placeholder="Type a reply… (sent through RingCentral, attached to the conversation)"
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
              data-testid="rc-inbox-reply-text"
            />
            <div className="flex items-center justify-between mt-1.5 gap-2">
              <span className="text-[10px] text-gray-400">
                {replyText.length}/1500 · Sending a reply does <b>not</b> archive this item — register the activity in the order to close it.
              </span>
              <button
                onClick={sendReply}
                disabled={replySending || !replyText.trim()}
                className="px-3 py-1.5 bg-[#6B8F62] hover:bg-[#5A7B52] disabled:opacity-50 text-white text-sm font-semibold rounded-md flex items-center gap-1.5 shrink-0"
                data-testid="rc-inbox-reply-send-btn"
              >
                <Send className="h-3.5 w-3.5" />
                {replySending ? 'Sending…' : 'Send Reply'}
              </button>
            </div>
            {replyResult && replyResult.ok && (
              <p className="text-[11px] text-emerald-700 mt-1.5" data-testid="rc-inbox-reply-success">
                ✅ Reply sent from {replyResult.received_by_name} to {replyResult.to_number}.
              </p>
            )}
            {replyResult && !replyResult.ok && (
              <p className="text-[11px] text-red-600 mt-1.5">{replyResult.error}</p>
            )}
          </div>
        )}

        {/* Linked order section */}
        <div className="px-4 pb-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1.5">Linked Order</p>
          {selectedOrder ? (
            <div className="border-2 border-[#6B8F62] rounded-lg p-3 bg-[#6B8F62]/5" data-testid="rc-inbox-linked-order">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-800">#{selectedOrder.order_number}</div>
                  <div className="text-xs text-gray-500 truncate">{selectedOrder.address}{selectedOrder.city ? `, ${selectedOrder.city}` : ''}</div>
                  {(selectedOrder.ph_name || selectedOrder.company || selectedOrder.status) && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {selectedOrder.ph_name && <>👤 {selectedOrder.ph_name} · </>}
                      {selectedOrder.company} {selectedOrder.status && <>· {selectedOrder.status}</>}
                      {selectedOrder.flow_type && <> · {selectedOrder.flow_type}</>}
                      {selectedOrder.due_date && <> · due {String(selectedOrder.due_date).slice(0,10)}</>}
                    </div>
                  )}
                </div>
                <button onClick={unlinkOrder} className="text-xs text-gray-500 hover:text-gray-800" data-testid="rc-inbox-unlink-btn">Change</button>
              </div>
              <button onClick={openOrder}
                className="mt-3 w-full px-3 py-2 bg-[#6B8F62] hover:bg-[#5A7B52] text-white text-sm font-semibold rounded-md flex items-center justify-center gap-2"
                data-testid="rc-inbox-open-order-btn">
                <ExternalLink className="h-4 w-4" />
                Open Order
              </button>
              <p className="text-[10px] text-gray-500 mt-1.5 text-center">
                Open the full order to review details before registering activity.
              </p>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by order #, address, city, phone, name, company…"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="rc-inbox-order-search"
                />
              </div>
              {searching && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
              {!searching && search.length >= 2 && searchResults.length === 0 && (
                <p className="text-xs text-gray-400 mt-1">No orders found.</p>
              )}
              {searchResults.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-md max-h-56 overflow-y-auto" data-testid="rc-inbox-search-results">
                  {searchResults.map((o) => (
                    <button key={o.order_id} onClick={() => linkOrder(o)} disabled={linking}
                      className="w-full text-left p-2 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center gap-2"
                      data-testid="rc-inbox-search-result-row">
                      <Link2 className="h-3.5 w-3.5 text-[#6B8F62] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800">#{o.order_number} · {o.address}</div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {o.city}{o.state ? ', ' + o.state : ''} · {o.ph_name || '—'} · {o.company || ''} · {o.status || ''}
                          {o.flow_type ? ` · ${o.flow_type}` : ''}{o.due_date ? ` · due ${String(o.due_date).slice(0,10)}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Standalone archive option (e.g., spam, wrong number with no order) */}
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 flex items-center justify-between">
          <p className="text-[11px] text-gray-500">No order to attach? Archive without registering.</p>
          <button onClick={archive} disabled={archiving}
            className="text-xs px-2.5 py-1.5 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 rounded-md flex items-center gap-1"
            data-testid="rc-inbox-archive-btn">
            <ArchiveIcon className="h-3.5 w-3.5" />
            {archiving ? 'Archiving…' : 'Archive'}
          </button>
        </div>

        {error && (
          <div className="px-4 pb-3 text-xs text-red-600" data-testid="rc-inbox-error">{error}</div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// SMS Inbox
// ─────────────────────────────────────────────────────────────────────────
export const RcSmsInbox = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [direction, setDirection] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [status, setStatus] = useState('');
  const [openItem, setOpenItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (direction) params.direction = direction;
      if (receivedBy) params.received_by = receivedBy;
      if (status) params.status = status;
      const r = await axios.get(`${API}/rc-inbox/sms`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error('Load RC SMS failed', e); }
    finally { setLoading(false); }
  }, [direction, receivedBy, status]);

  useEffect(() => { load(); }, [load]);

  const triggerSync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/rc-inbox/sync-now?days_back=7`); await load(); }
    catch (e) { console.error('Sync failed', e); }
    finally { setSyncing(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="rc-sms-inbox-page">
      <RcInboxLayoutHeader
        title="RC SMS Inbox" icon={<MessageSquare className="h-5 w-5 text-[#6B8F62]" />}
        total={total} syncing={syncing} onSync={triggerSync} onBack={() => navigate('/scheduling')}
      />
      <FilterBar direction={direction} setDirection={setDirection}
        receivedBy={receivedBy} setReceivedBy={setReceivedBy}
        status={status} setStatus={setStatus} showDirection />

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="rc-sms-inbox-table">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Dir.</th>
              <th className="text-left px-3 py-2">From</th>
              <th className="text-left px-3 py-2">Received by</th>
              <th className="text-left px-3 py-2">Text</th>
              <th className="text-left px-3 py-2">Linked order</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">No SMS items in this view. Click <b>Sync now</b> or change filters.</td></tr>
            )}
            {!loading && items.map((it) => (
              <tr key={it.rc_id} onClick={() => setOpenItem(it)}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" data-testid="rc-sms-row">
                <td className="px-3 py-2"><AppStatusPill status={it.app_status || 'NEW'} /></td>
                <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">{fmtDateTime(it.creation_time_rc)}</td>
                <td className="px-3 py-2"><Pill tone={it.direction === 'inbound' ? 'blue' : 'gray'}>{it.direction}</Pill></td>
                <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">{it.from_number || '—'}</td>
                <td className="px-3 py-2"><ReceivedByPill name={it.received_by_name} /></td>
                <td className="px-3 py-2 text-xs text-gray-700 max-w-md">
                  <div className="truncate" title={it.text}>{it.text || <span className="text-gray-400">(no text)</span>}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {it.matched_order_number
                    ? <span className="font-mono text-[#6B8F62]">#{it.matched_order_number}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        Click a row to open · Search/link order · Register activity (counts in your productivity) · Archive when done.
      </p>

      {openItem && (
        <RcDetailModal kind="sms" item={openItem}
          onClose={() => setOpenItem(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Voicemail Inbox
// ─────────────────────────────────────────────────────────────────────────
export const RcVoicemailInbox = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [receivedBy, setReceivedBy] = useState('');
  const [status, setStatus] = useState('');
  const [openItem, setOpenItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (receivedBy) params.received_by = receivedBy;
      if (status) params.status = status;
      const r = await axios.get(`${API}/rc-inbox/voicemail`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error('Load RC voicemail failed', e); }
    finally { setLoading(false); }
  }, [receivedBy, status]);

  useEffect(() => { load(); }, [load]);

  const triggerSync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/rc-inbox/sync-now?days_back=7`); await load(); }
    catch (e) { console.error('Sync failed', e); }
    finally { setSyncing(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="rc-voicemail-inbox-page">
      <RcInboxLayoutHeader
        title="RC Voicemail Inbox" icon={<Voicemail className="h-5 w-5 text-[#9A7A3E]" />}
        total={total} syncing={syncing} onSync={triggerSync} onBack={() => navigate('/scheduling')}
      />
      <FilterBar direction="" setDirection={() => {}}
        receivedBy={receivedBy} setReceivedBy={setReceivedBy}
        status={status} setStatus={setStatus} showDirection={false} />

      <div className="space-y-2" data-testid="rc-voicemail-inbox-list">
        {loading && <div className="text-center text-gray-400 py-8">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">
            No voicemail items in this view. Click <b>Sync now</b> or change filters.
          </div>
        )}
        {!loading && items.map((it) => (
          <div key={it.rc_id} onClick={() => setOpenItem(it)}
            className="bg-white rounded-lg border border-gray-200 hover:border-[#9A7A3E] p-3 cursor-pointer"
            data-testid="rc-vm-row">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap text-xs text-gray-700">
                  <AppStatusPill status={it.app_status || 'NEW'} />
                  <Phone className="h-3.5 w-3.5 text-gray-500" />
                  <span className="font-mono">{it.from_number || '—'}</span>
                  <span className="text-gray-300">·</span>
                  <ReceivedByPill name={it.received_by_name} />
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-500">{fmtDateTime(it.creation_time_rc)}</span>
                  {it.vm_duration ? (<><span className="text-gray-300">·</span><span className="text-gray-500">{it.vm_duration}s</span></>) : null}
                  <Pill tone={it.vm_transcription_status === 'Completed' ? 'green' : 'gray'}>
                    Transcription: {it.vm_transcription_status || '—'}
                  </Pill>
                  {it.matched_order_number && (<Pill tone="blue">#{it.matched_order_number}</Pill>)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        Click an item to open · Listen audio · Search/link order · Register activity (counts in your productivity) · Archive when done.
      </p>

      {openItem && (
        <RcDetailModal kind="voicemail" item={openItem}
          onClose={() => setOpenItem(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};
